/**
 * Object Explorer refresh bus — a tiny renderer-only signal store that lets
 * non-React code (e.g. the cross-engine migration run store) ask the Object
 * Explorer to re-fetch and rebuild a database's object subtree.
 *
 * The actual refresh (invalidate object caches → re-preload → re-apply schema
 * grouping) lives in `DatabaseExplorer`, which holds the React state and
 * fetch/restructure handlers a plain store can't reach. This bus is the seam:
 * a producer calls `requestRefresh(connectionId, database)`, which bumps
 * `nonce`; `DatabaseExplorer` subscribes to `nonce` and performs the refresh
 * using the target recorded here.
 *
 * Why a fresh re-preload rather than the per-folder Refresh action: a migration
 * can create a BRAND-NEW schema on the target. Schema-container folders are
 * derived from the database's preloaded object list, so only a full re-fetch +
 * re-group surfaces a new schema (refreshing already-expanded folders cannot).
 */
import { create } from 'zustand';

type ObjectExplorerRefreshState = {
  /** Bumped on every request — the explorer reacts to changes, ignoring 0. */
  nonce: number;
  /** Target of the most recent request. */
  connectionId: string | null;
  database: string | null;
  /** Ask the Object Explorer to refresh `database` under `connectionId`. */
  requestRefresh: (connectionId: string, database?: string) => void;
};

export const useObjectExplorerRefreshStore = create<ObjectExplorerRefreshState>((set) => ({
  nonce: 0,
  connectionId: null,
  database: null,
  requestRefresh: (connectionId, database) =>
    set((s) => ({ nonce: s.nonce + 1, connectionId, database: database ?? null })),
}));
